import { useCallback, useSyncExternalStore } from 'react';

import { ARTICLE_LINE_HEIGHT_SCALE_VAR } from '@/lib/article-renderer/core/font-scale';
import { LINE_HEIGHT_KEY } from '@/constants';

/**
 * The reader's line-spacing choice, as a multiplier on the publisher's own
 * line heights. 1.5 reaches WCAG 1.4.12's text-spacing ask on the tightest
 * styles publishers use (~1.0-1.2).
 */
// Labels live with the control that shows them (settings.lineSpacing.* in
// widgets/settings-dialog/i18n).
export const LINE_HEIGHT_OPTIONS = [
	{ value: 1 },
	{ value: 1.25 },
	{ value: 1.5 },
] as const;

export type LineHeightScale = (typeof LINE_HEIGHT_OPTIONS)[number]['value'];

const listeners = new Set<() => void>();

function read(): LineHeightScale {
	try {
		const stored = Number(localStorage.getItem(LINE_HEIGHT_KEY));
		const match = LINE_HEIGHT_OPTIONS.find((o) => o.value === stored);
		return match ? match.value : 1;
	} catch {
		return 1;
	}
}

/**
 * Sets the variable the article CSS multiplies line heights by. It lives on
 * <html> rather than on each renderer's root, so every article surface (the
 * reader, the article page, dialogs) follows one setting without the value
 * being threaded through each of them. Call once at startup, too, so a saved
 * choice applies before the first article renders.
 */
export function applyLineHeightScale(scale: LineHeightScale = read()) {
	document.documentElement.style.setProperty(
		ARTICLE_LINE_HEIGHT_SCALE_VAR,
		String(scale),
	);
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/** The saved line-spacing choice and its setter; every caller stays in sync. */
export function useLineHeight(): {
	lineHeight: LineHeightScale;
	setLineHeight: (scale: LineHeightScale) => void;
} {
	const lineHeight = useSyncExternalStore<LineHeightScale>(
		subscribe,
		read,
		() => 1,
	);
	const setLineHeight = useCallback((scale: LineHeightScale) => {
		try {
			localStorage.setItem(LINE_HEIGHT_KEY, String(scale));
		} catch {
			// Private mode etc.: still apply it for this session.
		}
		applyLineHeightScale(scale);
		listeners.forEach((listener) => listener());
	}, []);
	return { lineHeight, setLineHeight };
}
